import React, { Component } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Island, IslandButton, TrevorIcon } from '@trevor/ui-components';
import { StepType } from '@/views/QueryBuilder/subcomponents/Steps';
import {
  isButtonWithClass,
  isDropdownMenuItem,
} from '@/util/helpers/event-helpers';
import { userAction } from '@/util/helpers/ajax-helpers';
import {
  useInvalidStepId,
  useInvalidStepMessage,
} from '@/features/query-builder/hooks';
import { useSelector } from 'react-redux';
import { getQueryBuilderSteps } from '@/store-selectors';
import sidebar from '../../css/module/sidebar.css';
import flex from '../../css/module/flex.css';
import {
  ADD_COLUMN,
  AGGREGATE,
  CSV_FROM,
  EXPAND,
  FROM,
  FROM_SAVED_QUERY,
  GROUP_BY,
  JOIN,
  ORDER_BY,
  REARRANGE_COLUMNS,
  RENAME_COLUMNS,
  SELECT,
  SQL_FROM,
  WHERE,
} from '../../util/enums/EnumLayers';
import AddNewStep from '../AddNewStep';
import HighlightFeature, {
  ACTION_DISABLE,
  BOTTOM,
  ACTION_COMPLETE_SUMMARIZE,
  TOP,
  ACTION_FILTER,
  userIsFinishedOnboarding,
  isPublicDatasets,
} from '../HighlightFeature';
import {
  DELETE_STEP_BUTTON,
  DISABLE_STEP_BUTTON,
  QUERY_STEP,
} from '../../constants/test-ids';
import { MODAL_BUTTON_SELECTOR } from '../../containers/Modal';

class Step_LEGACY extends Component {
  constructor(props) {
    super(props);
    this.state = {
      showEffect: false,
      onboardingDelayed: false,
    };
    this.handleUserWindowClick = this.handleUserWindowClick.bind(this);
    this.layerContentContainer = React.createRef();
    this.activateEditMode = this.activateEditMode.bind(this);
  }

  componentDidMount() {
    window.addEventListener('click', this.handleUserWindowClick);
    if (this.props.isLastLayerAdded && this.props.active) {
      this.setState({
        showEffect: true,
      });
      this._timer = setTimeout(() => {
        this.setState({
          showEffect: false,
        });
      }, 800);
    }
  }

  componentWillUnmount() {
    // cleanup
    window.removeEventListener('click', this.handleUserWindowClick);
    clearTimeout(this._timer);
  }

  //
  // when user clicks outside this Step, we exit edit mode
  //
  handleUserWindowClick(e) {
    const { isEditMode, layer, onLeaveEditMode } = this.props;

    if (isEditMode && [FROM_SAVED_QUERY, FROM].includes(layer.type())) {
      onLeaveEditMode();
      return;
    }

    // if clicking inside the insert step button (half of which appears outside the step since it's sits on the upper border, just return out of function). Otherwise upper half of button can't be clicked
    if (isButtonWithClass(e.target, sidebar.addStepButton)) {
      return;
    }

    // if the insert button area is open, close it when user clicks outside
    if (this.props.lastOpened === layer.id() && this.isOutsideStep(e)) {
      this.props.onCloseAddStepButtons();
    }

    if (!isEditMode || layer.isTemplate()) {
      // this step isn't being edited, so ignore
      return;
    }

    if (e.target !== window) {
      // HACK: we only need this because DropdownButton fires such an event
      if (
        isDropdownMenuItem(e.target) ||
        isButtonWithClass(e.target, MODAL_BUTTON_SELECTOR)
      ) {
        // if user is interacting with menu or with a modal, don't close edit mode
        return;
      }
      if (this.isOutsideStep(e)) {
        // is outside this._step, so close edit mode
        onLeaveEditMode();
      }
    }
  }

  isOutsideStep(e) {
    const stepDims = this._step.getBoundingClientRect();
    // MouseEvent has clientX and clientY
    const x = e.clientX;
    const y = e.clientY;
    return (
      x < stepDims.x ||
      y < stepDims.y ||
      x > stepDims.x + stepDims.width ||
      y > stepDims.y + stepDims.height
    );
  }

  showDisableStepOnboarding() {
    const { layer, lastLayerNotInEditMode, actions } = this.props;
    return (
      lastLayerNotInEditMode === layer.id() &&
      actions &&
      !actions[ACTION_DISABLE]
    );
  }

  active() {
    const {
      stepNo,
      layer,
      onInteraction,
      onEdit,
      isLastActiveStep,
      isEditMode,
      actions,
    } = this.props;

    const onJumpToStep = (e) => {
      userAction('use-jump-to-step');
      onInteraction('selected', { layerId: layer.id() })(e);
    };

    const style = this.showDisableStepOnboarding() ? { display: 'flex' } : {};

    const isDeleteStepDisabled =
      stepNo === 1 || (!isEditMode && layer.enabled() && !layer.isTemplate());

    const isFirstStep = stepNo === 1;
    const userIsOnboarding = !userIsFinishedOnboarding(actions);

    return (
      <div className={sidebar.layerControl} style={style}>
        <Island size="small">
          {isEditMode ? null : (
            <IslandButton
              $disabled={this.showDisableStepOnboarding()}
              content={<TrevorIcon name="pen" />}
              tooltipContent="edit step"
              onClick={this.showDisableStepOnboarding() ? null : onEdit}
            />
          )}

          {isEditMode || isFirstStep || layer.enabled() === false ? null : (
            <IslandButton
              $disabled={stepNo === 1}
              data-test-id={`${DISABLE_STEP_BUTTON}:${stepNo}`}
              content={
                <>
                  <TrevorIcon name="checkbox-selected" />
                  {this.showDisableStepOnboarding() ? (
                    <HighlightFeature
                      key={stepNo + 1}
                      position={TOP}
                      text={
                        <span>
                          Click here to <b>disable</b> a step
                        </span>
                      }
                      targetAction={ACTION_DISABLE}
                      hideButton
                      style={{ minWidth: '230px' }}
                      delay={0}
                      hideWhenModalsOpen
                    />
                  ) : null}
                </>
              }
              tooltipContent={
                stepNo === 1 ? "can't disable first step" : 'disable step'
              }
              onClick={onInteraction('toggled', { layerId: layer.id() })}
            />
          )}
          {isEditMode || isFirstStep || layer.enabled() ? null : (
            <IslandButton
              content={<TrevorIcon name="checkbox-unselected" />}
              tooltipContent="Enable step"
              onClick={onInteraction('toggled', { layerId: layer.id() })}
            />
          )}

          <IslandButton
            $disabled={isDeleteStepDisabled || userIsOnboarding}
            data-test-id={`${DELETE_STEP_BUTTON}:${stepNo}`}
            content={<TrevorIcon name="trash-bin" />}
            tooltipContent={
              stepNo === 1
                ? "click 'start new query' to start again"
                : !isEditMode && layer.enabled() && !layer.isTemplate()
                ? 'disable before deleting'
                : 'delete step'
            }
            onClick={
              !isDeleteStepDisabled && !userIsOnboarding
                ? onInteraction('deleted', { layerId: layer.id() })
                : undefined
            }
          />
          <IslandButton
            $disabled={isLastActiveStep || userIsOnboarding}
            content={<TrevorIcon name="target" />}
            tooltipContent={
              isLastActiveStep
                ? 'already last active step'
                : 'deactivate steps below'
            }
            onClick={
              !isLastActiveStep && !userIsOnboarding ? onJumpToStep : undefined
            }
          />

          {/* Open insert step buttons */}
          {isLastActiveStep || this.props.lastOpened === layer.id() ? null : ( // if already open, don't show
            <IslandButton
              $disabled={userIsOnboarding}
              content={<TrevorIcon name="plus" />}
              tooltipContent="insert step below"
              onClick={() =>
                !this.state.showEffect &&
                !userIsOnboarding &&
                this.props.onOpenAddStepButtons()
              }
            />
          )}

          {/* Close insert step buttons */}
          {!isLastActiveStep && this.props.lastOpened !== layer.id() ? null : ( // if not open, don't show. If open, show
            <IslandButton
              $disabled={isLastActiveStep}
              content={
                <TrevorIcon
                  name="plus"
                  style={{
                    transform: isLastActiveStep ? 'none' : 'rotate(45deg)',
                  }}
                />
              }
              tooltipContent={
                isLastActiveStep
                  ? 'to add a step click buttons below'
                  : 'hide insert step buttons'
              }
              onClick={() =>
                !isLastActiveStep && !this.state.showEffect
                  ? this.props.onCloseAddStepButtons()
                  : undefined
              }
            />
          )}
        </Island>
      </div>
    );
  }

  inactive() {
    const { layer, onInteraction } = this.props;
    return (
      <div className={sidebar.reactiveLayer}>
        <div className={sidebar.layerControl}>
          <Island size="small">
            <IslandButton
              tooltipContent="Delete step"
              content={<TrevorIcon name="trash-bin" />}
              onClick={onInteraction('deleted', { layerId: layer.id() })}
            />
            <IslandButton
              tooltipContent="Reactivate to this step"
              content={<TrevorIcon name="target" />}
              onClick={onInteraction('selected', { layerId: layer.id() })}
              $disabled
            />
            <IslandButton
              tooltipContent="Reactivate to insert step"
              content={<TrevorIcon name="plus" />}
              $disabled
            />
          </Island>
        </div>
      </div>
    );
  }

  type() {
    const { layer } = this.props;
    const type = layer.layerType();
    switch (type) {
      case FROM:
      case FROM_SAVED_QUERY:
      case SQL_FROM:
      case CSV_FROM:
        return 'Start from';
      case WHERE:
        return 'Filter';
      case SELECT:
        return 'Show';
      case AGGREGATE:
        return 'Summarize';
      case ORDER_BY:
        return 'Sort';
      case JOIN:
        return 'Join';
      case RENAME_COLUMNS:
        return 'Rename';
      case REARRANGE_COLUMNS:
        return 'Reorder';
      case EXPAND:
        return 'Expand';
      case ADD_COLUMN:
        return layer.type() === 'TEMPLATE'
          ? 'Add column'
          : layer.replaceExisting()
          ? 'Replace column'
          : 'Add column';
      case GROUP_BY:
        return 'Summarize as new column';
      default:
        return layer.layerType();
    }
  }

  activateEditMode() {
    const { isEditMode, onEdit } = this.props;
    if (isEditMode) {
      return null;
    }
    onEdit();
  }

  render() {
    const {
      children,
      active,
      stepNo,
      layer,
      isLastActiveStep,
      isEditMode,
      isLastLayerAdded,
      onInteraction,
      onMouseEnter,
    } = this.props;

    const layerClass = `${sidebar.layer} sidebarLayer layer ${
      layer.enabled() ? '' : 'toggled-off '
    }${layer.isTemplate() ? 'template-layer ' : ''}`;
    const innerLayerClass = `${sidebar.layerInner} ${
      active ? '' : sidebar.inactiveStep
    }`;

    const isLastLayerClass =
      isLastLayerAdded && active ? sidebar.lastLayer : '';

    const insertStepClasses = `position-relative ${sidebar.insertLayer} ${
      this.props.lastOpened === layer.id() ? sidebar.lastHovered : ''
    } ${this.props.transitionsOn ? sidebar.transitionsOn : ''}`;

    return (
      <div
        ref={(div) => (this._step = div)}
        className={`${layerClass} ${isLastLayerClass} position-relative`}
        onMouseEnter={onMouseEnter}
      >
        {layer.type() === AGGREGATE && isLastActiveStep ? (
          <HighlightFeature
            key={`${stepNo}${layer.functions().length}-${
              layer.keys().length
            }-${isEditMode}`}
            position={BOTTOM}
            text={
              layer.functions().length > 0 ? (
                <span>Now let's add a dimension</span>
              ) : layer.keys().length > 0 ? (
                <span>Now let's add a metric</span>
              ) : (
                <span>
                  Use a <b>Summarize step</b> to perform calculations and unlock
                  charts 📊
                </span>
              )
            }
            targetAction={ACTION_COMPLETE_SUMMARIZE}
            requiredActions={[ACTION_DISABLE]}
            delay={3000}
            hideButton
            style={{ minWidth: '280px' }}
          />
        ) : null}
        {layer.isTemplate() &&
          layer.layerType() === WHERE &&
          !isPublicDatasets() && (
            <HighlightFeature
              key={stepNo + 1}
              position={BOTTOM}
              text={<span>Let's add a filter step</span>}
              targetAction={ACTION_FILTER}
              delay={5000}
              hideButton
              style={{ minWidth: '200px' }}
            />
          )}
        <div className={innerLayerClass}>
          {this.state.showEffect ? (
            <div className={sidebar.pulsateContainer}>
              <span />
            </div>
          ) : null}
          <div className={sidebar.stepNumberContainer}>
            <div className={sidebar.stepNumber}>{stepNo}</div>
          </div>
          <div className={sidebar.layerTypeContainer}>
            <StepType onClick={isEditMode ? null : this.activateEditMode}>
              {this.type()}
            </StepType>
          </div>
          <div
            className={`layerContent ${sidebar.layerContent}`}
            ref={this.layerContentContainer}
          >
            <div data-test-id={`${QUERY_STEP}:${stepNo}:${this.type()}`}>
              {children}
            </div>
            <StepError stepId={layer._id} />
          </div>
        </div>
        {active && !isLastActiveStep ? (
          <div className={insertStepClasses}>
            <AddNewStep optionalLayer={layer} isDropdown={false} />
          </div>
        ) : null}
        {active ? (
          this.active()
        ) : (
          <InactiveStepsControls
            stepId={layer._id}
            onInteraction={onInteraction}
          />
        )}
      </div>
    );
  }
}

const StepError = ({ stepId }) => {
  const invalidStepId = useInvalidStepId();
  const invalidStepMessage = useInvalidStepMessage();

  if (invalidStepId !== stepId) return null;

  return (
    <div className={`alert alert-danger margin-top-10 ${sidebar.error}`}>
      <div className={flex.alignItemsStart}>
        <div className="margin-right-10">
          <FontAwesomeIcon icon="bomb" />
        </div>
        {invalidStepMessage}
      </div>
    </div>
  );
};

const InactiveStepsControls = ({ stepId: layerId, onInteraction }) => {
  const steps = useSelector(getQueryBuilderSteps);
  const invalidStepId = useInvalidStepId();

  const lastStepToShowIndex =
    steps.findIndex((s) => s._id === invalidStepId) + 1 || Infinity;

  const affectedSteps = invalidStepId ? steps.slice(lastStepToShowIndex) : [];

  const reactivationDisabled = !!affectedSteps.find((s) => s._id === layerId);

  return (
    <div className={sidebar.reactiveLayer}>
      <div className={sidebar.layerControl}>
        <Island size="small">
          <IslandButton
            tooltipContent="Delete step"
            content={<TrevorIcon name="trash-bin" />}
            onClick={onInteraction('deleted', { layerId })}
          />
          <IslandButton
            tooltipContent="Reactivate to this step"
            content={<TrevorIcon name="target" />}
            onClick={
              reactivationDisabled
                ? undefined
                : onInteraction('selected', { layerId })
            }
            $disabled={reactivationDisabled}
          />
          <IslandButton
            tooltipContent="Reactivate to insert step"
            content={<TrevorIcon name="plus" />}
            $disabled
          />
        </Island>
      </div>
    </div>
  );
};

export default Step_LEGACY;
